import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, User } from 'lucide-react';
import BRAND from '@/brand';
import Wordmark from '@/components/Wordmark';

// DigitalBox' forside, bygget efter XD-artboards.
//
// Farverne staar som literaler her frem for i brand-tokens, fordi de kun
// findes paa denne side og kun for dette brand — samme maade hentposts forside
// haandterer sine moerke flader. Brandets teal kommer derimod fra tokens, saa
// den kun defineres ét sted.
const SIDE_BG = '#F0F0F0';   // Light Grey fra brandpaletten
const DIS = '#DCE7E2';       // bleg salvie til de rolige sektioner
const NAVY = '#1A1A2E';      // Dark Navy

// Understreger ordet i {tuborgklammer}, som overskrifterne gør i designet.
function Fremhaev({ tekst, className = '' }) {
    const dele = tekst.split(/\{([^}]+)\}/);
    return (
        <>
            {dele.map((del, i) =>
                i % 2 === 1
                    ? <u key={i} className={`decoration-4 underline-offset-[6px] ${className}`}>{del}</u>
                    : <React.Fragment key={i}>{del}</React.Fragment>
            )}
        </>
    );
}

// Det grafiske motiv i heroens hoejre side: en stor cirkel og et raster af
// lodrette bjaelker i aftagende teal. Rent dekorativt, derfor aria-hidden.
function HeroMotiv() {
    // Aftagende tealtoner fra venstre mod hoejre, saa rasteret toner ind mod
    // kanten i stedet for at skaere billedet over.
    const bjaelker = [0.06, 0.10, 0.15, 0.21, 0.28, 0.36, 0.30, 0.20];
    return (
        <svg
            className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-[30%] lg:block"
            viewBox="0 0 400 800" preserveAspectRatio="xMaxYMid slice" aria-hidden="true"
        >
            <circle cx="300" cy="400" r="300" fill="#ffffff" opacity="0.07" />
            {bjaelker.map((o, i) => (
                <rect
                    key={i} x={16 + i * 48} y="90" width="34" height="620"
                    fill="rgb(var(--brand-500))" opacity={o}
                />
            ))}
        </svg>
    );
}

// Pilleformet knap. Designet bruger tre varianter: lys omrids paa foto,
// fyldt teal, og fyldt navy.
function Pille({ variant = 'teal', href, children, className = '', ...rest }) {
    const basis = 'inline-flex items-center justify-center rounded-full px-7 py-3 '
        + 'text-[15px] font-semibold transition-colors focus-visible:outline-none '
        + 'focus-visible:ring-2 focus-visible:ring-offset-2';
    const varianter = {
        omrids: 'border-2 border-white text-white hover:bg-white hover:text-[#1A1A2E] focus-visible:ring-white',
        teal: 'bg-btn text-btn-fg hover:bg-btn-hover focus-visible:ring-brand-600',
        navy: 'bg-[#1A1A2E] text-white hover:bg-[#33334d] focus-visible:ring-[#1A1A2E]',
        hvid: 'bg-white text-[#1A1A2E] hover:bg-[#e9e9ee] focus-visible:ring-white',
    };
    // className flettes ind til sidst. Blev den i stedet spredt med {...rest},
    // ville den overskrive hele klassestrengen og knappen miste sin flade.
    const klasse = `${basis} ${varianter[variant]} ${className}`.trim();
    // Interne ruter gaar gennem react-router; ankre og mailto skal vaere <a>.
    const internt = href?.startsWith('/');
    return internt
        ? <Link to={href} className={klasse} {...rest}>{children}</Link>
        : <a href={href} className={klasse} {...rest}>{children}</a>;
}

function Header() {
    return (
        <header className="sticky top-0 z-50 border-b border-black/5 bg-white/95 backdrop-blur">
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-4 sm:px-6">
                <Link to="/" className="text-2xl font-bold text-[#1A1A2E] sm:text-3xl" aria-label={BRAND.name}>
                    <Wordmark />
                </Link>

                <nav className="hidden items-center gap-8 lg:flex" aria-label="Hovedmenu">
                    {BRAND.landing.nav.map((n) => (
                        <a key={n.href} href={n.href}
                           className="text-[15px] text-[#4b4b5a] transition-colors hover:text-[#1A1A2E]">
                            {n.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Pille variant="navy" href="/register" className="!px-6 !py-2.5">Kom igang</Pille>
                    <Link
                        to="/login"
                        aria-label="Log ind"
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500
                                   text-white transition-colors hover:bg-brand-600"
                    >
                        <User size={20} />
                    </Link>
                </div>
            </div>
        </header>
    );
}

function Hero() {
    const { hero } = BRAND.landing;
    // Hoejden foelger viewporten op til et loft, saa fotoet ikke bliver zoomet
    // uigenkendeligt paa meget hoeje skaerme. Loftet ligger inde i min-height
    // som min(): et separat max-height ville blive ignoreret, fordi CSS lader
    // min-height vinde naar de to er i konflikt.
    return (
        <section className="relative isolate flex min-h-[min(86vh,880px)] items-center overflow-hidden">
            <img
                src="/brand/digitalbox/img/hero.webp"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 -z-20 h-full w-full object-cover"
            />
            {/* Overlay: kraftigst i venstre side hvor teksten staar, saa
                overskriften har kontrast uanset hvad fotoet viser dér. */}
            <div
                className="absolute inset-0 -z-10"
                style={{
                    background: 'linear-gradient(90deg, rgba(26,26,46,.90) 0%, '
                        + 'rgba(26,26,46,.78) 45%, rgba(26,26,46,.52) 100%)',
                }}
            />
            <HeroMotiv />

            <div className="mx-auto w-full max-w-7xl px-4 py-24 sm:px-6">
                <div className="max-w-2xl">
                    <h1 className="font-heading text-4xl font-bold leading-[1.1] text-white sm:text-5xl lg:text-6xl">
                        <Fremhaev tekst={hero.overskrift} />
                    </h1>
                    <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/90">
                        {hero.broedtekst}
                    </p>
                    <div className="mt-9 flex flex-wrap gap-4">
                        <Pille variant="omrids" href={hero.primaer.href}>{hero.primaer.label}</Pille>
                        <Pille variant="teal" href={hero.sekundaer.href}>{hero.sekundaer.label}</Pille>
                    </div>
                </div>
            </div>

            <a
                href="#om"
                aria-label="Videre til indholdet"
                className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/80
                           transition-colors hover:text-white"
            >
                <ChevronDown size={40} strokeWidth={2.5} />
            </a>
        </section>
    );
}

// De to delte sektioner er spejlvendinger af hinanden: billede og tekstpanel
// bytter side, og panelet skifter mellem teal og bleg salvie. Det store
// hjoerneradius sidder i det hjoerne der vender ind mod midten.
function Delt({ id, billede, alt, panel, side }) {
    const billedeFoerst = side === 'venstre';
    return (
        <section id={id} className="py-16 sm:py-24" style={{ backgroundColor: SIDE_BG }}>
            <div className="mx-auto grid max-w-7xl items-stretch gap-6 px-4 sm:px-6 lg:grid-cols-2">
                <div className={`overflow-hidden ${billedeFoerst
                    ? 'rounded-bl-[4rem] lg:order-1' : 'rounded-tr-[4rem] lg:order-2'}`}>
                    <img src={billede} alt={alt}
                         className="h-full min-h-[280px] w-full object-cover" loading="lazy" />
                </div>

                <div
                    className={`flex flex-col justify-center p-8 sm:p-12 ${billedeFoerst
                        ? 'rounded-tr-[4rem] lg:order-2' : 'rounded-bl-[4rem] lg:order-1'}`}
                    style={panel.moerk
                        ? { backgroundColor: 'rgb(var(--brand-500))' }
                        : { backgroundColor: DIS }}
                >
                    <h2 className={`font-heading text-3xl font-bold leading-tight sm:text-4xl
                                    ${panel.moerk ? 'text-white' : 'text-[#1A1A2E]'}`}>
                        <Fremhaev tekst={panel.overskrift} />
                    </h2>
                    <p className={`mt-5 max-w-md text-[17px] leading-relaxed
                                   ${panel.moerk ? 'text-white/90' : 'text-[#3d3d4d]'}`}>
                        {panel.broedtekst}
                    </p>
                    <div className="mt-8">
                        <Pille variant={panel.moerk ? 'hvid' : 'navy'} href={panel.knap.href}>
                            {panel.knap.label}
                        </Pille>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Faq() {
    const { faq } = BRAND.landing;
    return (
        <section id="faq" className="py-16 sm:py-24" style={{ backgroundColor: SIDE_BG }}>
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                {/* Trappelinjen fra designet: label, vandret streg, trin ned */}
                <div className="mb-12 flex items-center gap-6" aria-hidden="true">
                    <span className="text-xs font-semibold tracking-[0.3em] text-[#9a9aa5]">FAQ</span>
                    {/* min-w-0: uden den kan et flex-element ikke krympe under sin
                        iboende bredde, og denne SVG's viewBox paa 800 ville skubbe
                        hele siden ud i vandret scroll paa mobil. */}
                    <svg className="h-16 min-w-0 flex-1" viewBox="0 0 800 64" preserveAspectRatio="none">
                        <path d="M0 8 H620 V56 H800" fill="none" stroke="#c9c9d1" strokeWidth="1.5"
                              vectorEffect="non-scaling-stroke" />
                    </svg>
                </div>

                <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
                    <div>
                        <h2 className="font-heading text-4xl font-bold text-[#1A1A2E] sm:text-5xl">
                            {faq.overskrift}
                        </h2>
                        <dl className="mt-12 space-y-10">
                            {faq.punkter.map((p) => (
                                <div key={p.titel} className="flex gap-5">
                                    <span className="mt-1.5 h-6 w-6 shrink-0 rounded-full bg-brand-500" aria-hidden="true" />
                                    <div>
                                        <dt className="font-heading text-xl font-bold text-[#1A1A2E]">{p.titel}</dt>
                                        <dd className="mt-3 text-[16px] leading-relaxed text-[#4b4b5a]">{p.svar}</dd>
                                    </div>
                                </div>
                            ))}
                        </dl>
                    </div>

                    <div className="overflow-hidden rounded-bl-[4rem] lg:sticky lg:top-28">
                        <img src="/brand/digitalbox/img/tablet.webp"
                             alt="Bilag og nøgletal vist på en tablet"
                             className="h-full w-full object-cover" loading="lazy" />
                    </div>
                </div>
            </div>
        </section>
    );
}

function Footer() {
    const { footer } = BRAND.landing;
    return (
        <footer id="kontakt" className="pt-16 pb-12" style={{ backgroundColor: DIS }}>
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
                    <div>
                        <div className="text-5xl font-bold text-[#1A1A2E] sm:text-6xl">
                            <Wordmark />
                        </div>
                        <p className="mt-8 max-w-xl text-[15px] leading-relaxed text-[#4b4b5a]">
                            <strong className="text-[#1A1A2E]">{BRAND.name}</strong> {footer.beskrivelse}
                        </p>
                    </div>

                    {footer.kolonner.map((k) => (
                        <div key={k.titel}>
                            <h3 className="font-heading text-2xl font-bold text-[#1A1A2E]">{k.titel}</h3>
                            <ul className="mt-5 space-y-2.5">
                                {k.links.map((l) => (
                                    <li key={l.href}>
                                        {l.href.startsWith('/') ? (
                                            <Link to={l.href}
                                                  className="text-[15px] text-[#4b4b5a] transition-colors hover:text-[#1A1A2E]">
                                                {l.label}
                                            </Link>
                                        ) : (
                                            <a href={l.href}
                                               className="text-[15px] text-[#4b4b5a] transition-colors hover:text-[#1A1A2E]">
                                                {l.label}
                                            </a>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-14 border-t border-black/10 pt-6 text-sm text-[#6b6b78]">
                    <p>&copy; {new Date().getFullYear()} {BRAND.copyrightName}. Alle rettigheder forbeholdes.</p>
                    <p className="mt-1">{BRAND.ownerLine}</p>
                </div>
            </div>
        </footer>
    );
}

export default function DigitalboxLanding() {
    const { om, saadan } = BRAND.landing;
    return (
        <div style={{ backgroundColor: SIDE_BG, color: NAVY }}>
            <Header />
            <main>
                <Hero />
                <Delt
                    id="om" side="venstre"
                    billede="/brand/digitalbox/img/kontor.webp"
                    alt="Kontor med skrivebord, reoler og planter"
                    panel={{ ...om, moerk: true }}
                />
                <Delt
                    id="saadan" side="hoejre"
                    billede="/brand/digitalbox/img/mand.webp"
                    alt="Mand ved skrivebord der tjekker post på sin telefon"
                    panel={{ ...saadan, moerk: false }}
                />
                <Faq />
            </main>
            <Footer />
        </div>
    );
}
